import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';

// ── Frequency options with descriptive labels ──────────────────────
// The numeric values preserve scoring while the labels are child-friendly
const FREQ_HIGH = [
  { value: '3', label: 'Not really' }, { value: '6', label: 'Sometimes' },
  { value: '9', label: 'Quite often' }, { value: '12', label: 'All the time' },
];
const FREQ_MED = [
  { value: '2', label: 'Not really' }, { value: '4', label: 'Sometimes' },
  { value: '6', label: 'Quite often' }, { value: '8', label: 'All the time' },
];
const FREQ_LOW = [
  { value: '1', label: 'Not really' }, { value: '2', label: 'Sometimes' },
  { value: '3', label: 'Quite often' }, { value: '4', label: 'All the time' },
];
const STRENGTH_OPTIONS = [
  { value: '1', label: 'Not really' }, { value: '2', label: 'Sometimes' },
  { value: '3', label: 'Quite often' }, { value: '4', label: 'Definitely!' },
];
const YES_NO_UNSURE = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unsure', label: 'Not sure' },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const sectionSub = { fontSize: '0.8125rem', color: '#6b7280', marginBottom: 16 };
const fieldGap = { marginBottom: 20 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };

// ── Component ──────────────────────────────────────────────────────
export default function UnderstandingLearningForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Background ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Background</h2>
        <div style={fieldGap}>
          <ChipSelector label="Have you had any help before to address learning difficulties?" options={YES_NO_UNSURE} value={r.previous_help || ''} onChange={v => set('previous_help', v)} />
        </div>
        {r.previous_help === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>When did you receive this help?</label>
            <textarea style={textareaStyle} value={r.help_details || ''} onChange={e => set('help_details', e.target.value)} placeholder="e.g. what year or how old were you?" />
          </div>
        )}
      </div>

      {/* ─── Reading ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Reading</h2>
        <p style={sectionSub}>Think about when you read — books, screens, worksheets, anything. How often do these things happen?</p>
        <div style={fieldGap}>
          <ChipSelector label="When I'm reading, do words that look alike (like 'cat' and 'cot') get mixed up?" options={FREQ_HIGH} value={r.reading_similar_words || ''} onChange={v => set('reading_similar_words', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="When I'm reading, do I sometimes lose my place or skip lines?" options={FREQ_MED} value={r.reading_lose_place || ''} onChange={v => set('reading_lose_place', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Do I need to read things more than once to understand them?" options={FREQ_LOW} value={r.reading_reread || ''} onChange={v => set('reading_reread', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Is it hard for me to sound out words (like breaking 'elephant' into 'el-e-phant')?" options={FREQ_HIGH} value={r.reading_sounding_out || ''} onChange={v => set('reading_sounding_out', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Do I find it difficult to read out loud?" options={FREQ_LOW} value={r.reading_aloud || ''} onChange={v => set('reading_aloud', v)} />
        </div>
      </div>

      {/* ─── Language & Processing ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Language and Processing</h2>
        <div style={fieldGap}>
          <ChipSelector label="Do I sometimes mix up the names of things (like saying 'table' when I mean 'chair')?" options={FREQ_LOW} value={r.word_mixup || ''} onChange={v => set('word_mixup', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="When someone gives me lots of instructions at once, do I get confused?" options={FREQ_LOW} value={r.instruction_confusion || ''} onChange={v => set('instruction_confusion', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Do I make mistakes when writing down phone messages or notes?" options={FREQ_LOW} value={r.note_errors || ''} onChange={v => set('note_errors', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Is it hard for me to think of the right word when I'm talking?" options={FREQ_LOW} value={r.word_finding || ''} onChange={v => set('word_finding', v)} />
        </div>
      </div>

      {/* ─── Direction & Spatial ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Direction and Space</h2>
        <div style={fieldGap}>
          <ChipSelector label="Do I have trouble telling left from right?" options={FREQ_LOW} value={r.left_right || ''} onChange={v => set('left_right', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Is reading maps or finding my way to new places tricky for me?" options={FREQ_LOW} value={r.maps_navigation || ''} onChange={v => set('maps_navigation', v)} />
        </div>
      </div>

      {/* ─── Writing & Memory ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Writing and Memory</h2>
        <div style={fieldGap}>
          <ChipSelector label="When I'm writing, is it hard to organise my thoughts and put them on paper?" options={FREQ_MED} value={r.writing_organising || ''} onChange={v => set('writing_organising', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Was learning my times tables (multiplication) difficult for me?" options={FREQ_MED} value={r.times_tables || ''} onChange={v => set('times_tables', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Is it hard for me to say the alphabet in order?" options={FREQ_LOW} value={r.alphabet_order || ''} onChange={v => set('alphabet_order', v)} />
        </div>
      </div>

      {/* ─── Strengths ─── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Your Strengths</h2>
        <p style={sectionSub}>This one's about the good stuff!</p>
        <div style={fieldGap}>
          <ChipSelector label="Do I come up with creative or different ways to solve problems?" options={STRENGTH_OPTIONS} value={r.creative_solving || ''} onChange={v => set('creative_solving', v)} />
        </div>
      </div>
    </div>
  );
}
